import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import Button from 'fe-design-base/molecules/Button';
import { isEmpty, partial } from 'lodash';
import PropTypes from 'prop-types';

import TextInput from 'components/forms/TextInput';

import { cxHelpers } from 'util/className';
import { trackUxEvent } from 'util/tracking';
import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  PRODUCT_AREAS,
} from 'util/tracking_constants';

import { addShiftNoShowReason, submitManagerNote } from '../slices';

const { cx, cxEl } = cxHelpers('AddNote');

function trackV3NoteSubmit() {
  trackUxEvent({
    productArea: PRODUCT_AREAS.TIMESHEETS,
    eventCategory: EVENT_CATEGORIES.TIMECARD_DETAIL,
    eventAction: EVENT_ACTIONS.TIMECARD_NOTE_ADDED_V3,
  });
}

const AddNote = ({
  // eslint-disable-next-line no-shadow
  submitManagerNote,
  // eslint-disable-next-line no-shadow
  addShiftNoShowReason,
  noShowReason,
  placeholder,
  shiftId,
}) => {
  const [body, setBody] = useState('');

  function submitNoShowReason() {
    if (noShowReason) {
      addShiftNoShowReason({
        reason: noShowReason,
        id: shiftId,
        originPath: window.location.pathname,
      });
    }
  }

  const localStorageUniqueKey = `manager-notes-${shiftId}`;

  useEffect(() => {
    if (localStorage.getItem(localStorageUniqueKey)) {
      setBody(localStorage.getItem(localStorageUniqueKey));
    }
  }, [shiftId, localStorageUniqueKey]);

  const handleSetBody = useCallback(
    value => {
      if (value === '') {
        localStorage.removeItem(localStorageUniqueKey);
      }

      localStorage.setItem(localStorageUniqueKey, value);
      setBody(value);
    },
    [localStorageUniqueKey]
  );

  // eslint-disable-next-line no-shadow
  function handleSubmit(body) {
    if (body && body !== '') {
      trackV3NoteSubmit();
      submitManagerNote({
        note: { body, noShow: !isEmpty(noShowReason) },
        id: shiftId,
        originPath: window.location.pathname,
      }).then(submitNoShowReason);
    } else if (noShowReason) {
      submitNoShowReason();
    }

    handleSetBody('');
  }

  return (
    <Box
      className={cx()}
      mb8={noShowReason}
      mh8={noShowReason}
      mt={16}
      w={noShowReason ? 250 : null}
    >
      <TextInput
        type="textarea"
        value={body}
        onChange={handleSetBody}
        inputProps={{
          style: {
            fontSize: 14,
            color: 'black',
            resize: 'none',
            padding: 8,
            minHeight: 49,
            height: 49,
            maxHeight: 49,
            lineHeight: '21px',
          },
        }}
        placeholder={placeholder}
        valueOnChange
      />
      <Box tright mt={8}>
        <Button
          variant="primary"
          className={cxEl('submit')}
          fullWidth={!!noShowReason}
          disabled={isEmpty(body.trim()) && !noShowReason}
          onClick={partial(handleSubmit, body)}
          size="small"
        >
          <Text
            variant="bodyBold"
            i18n={
              noShowReason
                ? 'actions.submit'
                : 'team.employee_profile.notes.add'
            }
          />
        </Button>
      </Box>
    </Box>
  );
};

AddNote.propTypes = {
  submitManagerNote: PropTypes.func.isRequired,
  addShiftNoShowReason: PropTypes.func.isRequired,
  noShowReason: PropTypes.string,
  placeholder: PropTypes.string.isRequired,
  shiftId: PropTypes.number.isRequired,
};

export default connect(() => ({}), { submitManagerNote, addShiftNoShowReason })(
  AddNote
);
